// src/lib/richtext/htmlSanitizer.js
import DOMPurify from 'dompurify'

// Allowed protocols for hrefs — keep editor and viewer in sync.
const ALLOWED_URI_REGEXP = /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|cid|xmpp):|[^a-z]|[a-z+.-]+(?:[^a-z+.-:]|$))/i

const BASE_ALLOWED_TAGS = [
  'p', 'br', 'hr', 'strong', 'b', 'em', 'i', 'u', 's', 'strike',
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'ul', 'ol', 'li',
  'blockquote', 'pre', 'code',
  'a', 'span', 'mark',
  'img', 'figure', 'figcaption',
]

// Limit inline styles to the few properties our editor actually produces.
// DOMPurify lets arbitrary CSS through inside style="" by default, so we
// whitelist explicitly to shrink the XSS surface and the payload size.
const SAFE_STYLE_PROPS = /^\s*(text-align|color|background-color)\s*$/i

export const normalizeRichTextUrl = (raw) => {
  const trimmed = (raw || '').trim()
  if (!trimmed) return null

  if (/^(mailto:|tel:|#|\/)/i.test(trimmed)) return trimmed

  const withScheme = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`

  try {
    const parsed = new URL(withScheme)
    if (!parsed.hostname || !parsed.hostname.includes('.')) return null
    return parsed.toString()
  } catch {
    return null
  }
}

let hookInstalled = false
const installStyleHook = () => {
  if (hookInstalled) return
  hookInstalled = true
  DOMPurify.addHook('uponSanitizeAttribute', (_node, data) => {
    if (data.attrName !== 'style') return
    const filtered = data.attrValue
      .split(';')
      .map(decl => decl.trim())
      .filter(Boolean)
      .filter(decl => {
        const idx = decl.indexOf(':')
        if (idx === -1) return false
        const prop = decl.slice(0, idx)
        return SAFE_STYLE_PROPS.test(prop)
      })
      .join('; ')
    if (filtered) {
      data.attrValue = filtered
    } else {
      data.keepAttr = false
    }
  })
}

const baseConfig = {
  USE_PROFILES: { html: true },
  ALLOWED_TAGS: [...BASE_ALLOWED_TAGS, 'div'],
  ALLOWED_ATTR: ['href', 'target', 'rel', 'title', 'style', 'class',
                 'src', 'alt', 'width', 'height', 'loading'],
  ALLOWED_URI_REGEXP,
  ALLOW_DATA_ATTR: true,
  KEEP_CONTENT: true,
  SANITIZE_DOM: true,
  ALLOW_UNKNOWN_PROTOCOLS: false,
}

const viewerConfig = {
  USE_PROFILES: { html: true },
  ALLOWED_TAGS: BASE_ALLOWED_TAGS,
  ALLOWED_ATTR: ['href', 'target', 'rel', 'style', 'class',
                 'src', 'alt', 'width', 'height', 'loading'],
  ALLOWED_URI_REGEXP,
  KEEP_CONTENT: true,
  SANITIZE_DOM: true,
}

/**
 * Sanitize HTML produced by the editor before persistence.
 * @param {string} html
 * @returns {string}
 */
export const sanitizeHtml = (html) => {
  try {
    if (!html) return ''
    installStyleHook()
    return DOMPurify.sanitize(html, baseConfig)
  } catch (error) {
    console.warn('Failed to sanitize HTML:', error)
    return ''
  }
}

/**
 * Sanitize HTML for read-only display (slightly tighter — no <div>).
 * @param {string} html
 * @returns {string}
 */
export const sanitizeHtmlForViewer = (html) => {
  try {
    if (!html) return ''
    installStyleHook()
    return DOMPurify.sanitize(html, viewerConfig)
  } catch (error) {
    console.warn('Failed to sanitize HTML for viewer:', error)
    return ''
  }
}
